/** Fallback portal checklist when capabilities omit `ibkr_flex_portal_setup`. */
export const IBKR_PORTAL_DATE_FORMAT = "yyyyMMdd";
export const IBKR_PORTAL_TIME_FORMAT = "HHmmss TimeZone";
/** Semantic value Tessera expects from IBKR portal exports. */
export const IBKR_PORTAL_DATETIME_SEPARATOR = "semi-colon";

/** Human-readable portal checklist values shown in setup UI. */
export function formatIbkrPortalSetupDisplayValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === "semi-colon" || normalized === "semicolon") {
    return "; (semi-colon)";
  }
  return value.trim();
}

export const IBKR_CORE_AUTH_FIELDS = ["ibkr_flex_token", "ibkr_query_id"] as const;

export interface IbkrFlexPortalSetupItem {
  label: string;
  value?: string;
}

export interface IbkrFlexPortalSetupInfo {
  summary?: string;
  items: IbkrFlexPortalSetupItem[];
}

const DEFAULT_PORTAL_ITEMS: IbkrFlexPortalSetupItem[] = [
  { label: "Date format", value: IBKR_PORTAL_DATE_FORMAT },
  { label: "Time format", value: IBKR_PORTAL_TIME_FORMAT },
  { label: "Date/time separator", value: IBKR_PORTAL_DATETIME_SEPARATOR },
];

function portalItemsFromFlatFields(o: Record<string, unknown>): IbkrFlexPortalSetupItem[] {
  const dateFormat = o.date_format != null ? String(o.date_format).trim() : "";
  const timeFormat = o.time_format != null ? String(o.time_format).trim() : "";
  const separator =
    o.date_time_separator != null ? String(o.date_time_separator).trim() : "";
  const items: IbkrFlexPortalSetupItem[] = [];
  if (dateFormat) items.push({ label: "Date format", value: dateFormat });
  if (timeFormat) items.push({ label: "Time format", value: timeFormat });
  if (separator) items.push({ label: "Date/time separator", value: separator });
  return items;
}

export function parseIbkrFlexPortalSetup(raw: unknown): IbkrFlexPortalSetupInfo {
  if (!raw || typeof raw !== "object") {
    return { items: DEFAULT_PORTAL_ITEMS };
  }
  const o = raw as Record<string, unknown>;
  const summary =
    typeof o.description === "string" && o.description.trim()
      ? o.description.trim()
      : typeof o.summary === "string" && o.summary.trim()
        ? o.summary.trim()
        : undefined;

  const flatItems = portalItemsFromFlatFields(o);
  if (flatItems.length > 0) {
    return { summary, items: flatItems };
  }

  const list = o.items ?? o.steps ?? o.checklist;
  if (Array.isArray(list) && list.length > 0) {
    const items = list
      .map((item) => {
        if (typeof item === "string" && item.trim()) {
          return { label: item.trim() };
        }
        if (!item || typeof item !== "object") return null;
        const row = item as Record<string, unknown>;
        const label = String(row.label ?? row.text ?? row.name ?? "").trim();
        const value =
          row.value != null
            ? String(row.value).trim()
            : row.format != null
              ? String(row.format).trim()
              : undefined;
        if (!label) return null;
        return { label, value: value || undefined };
      })
      .filter(Boolean) as IbkrFlexPortalSetupItem[];
    if (items.length > 0) {
      return { summary, items };
    }
  }

  return { summary, items: DEFAULT_PORTAL_ITEMS };
}

/**
 * True when the message is about IBKR Flex portal time format / timezone labels
 * (CSV, sync, test-connection, or stored creds with plain HHmmss).
 */
export function isIbkrFlexMissingTimezoneError(message: string): boolean {
  const m = message.toLowerCase();
  if (!m.trim()) return false;
  if (m.includes("timezone-aware portal")) return true;
  if (m.includes("unsupported time_format") && (m.includes("ibkr") || m.includes("timezone")))
    return true;
  return (
    m.includes("timezone") &&
    (m.includes("flex") ||
      m.includes("ibkr") ||
      m.includes("timestamp") ||
      m.includes("time format") ||
      m.includes("hhmmss"))
  );
}

export const IBKR_FLEX_TIMEZONE_ERROR_TITLE = "Flex query timezone required";

/** POST /broker/credentials — only Flex token and Query ID (account inferred from report). */
export function buildIbkrCredentialPayload(
  values: Record<string, string>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of IBKR_CORE_AUTH_FIELDS) {
    const v = String(values[key] || "").trim();
    if (v) out[key] = v;
  }
  return out;
}

export function isIbkrConnectCredentialField(field: string): boolean {
  return (IBKR_CORE_AUTH_FIELDS as readonly string[]).includes(field);
}
